/* eslint-disable @typescript-eslint/no-explicit-any */
import { PaymentMethodDetails } from 'utils/Analytics';
import { StoreType } from '@tock/shared/utils/connect';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { core, safeToNumber } from '@tock/coretypes';
import { noop, toNumber } from 'lodash';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const {
  AnsweredPurchaseQuestions,
  PurchaseOrder,
  PackagePurchaseOrder,
  CancellationRequest,
  VisitFeedback,
  OptionOrderUpdate,
} = protos;

export function bookViaEmail(purchaseOrder: core.IPurchaseOrder): ApiAction {
  return {
    TockApi: {
      postProto: '/consumer/concierge/send-verify-email',
      request: PurchaseOrder.fromObject(purchaseOrder),
      type: AppConstants.CONCIERGE_BOOK_VIA_EMAIL,
      toast:
        'Concierge email reservation invitation has been sent. Check your email to manage the invitation.',
    },
  };
}

export function cancelBookViaEmail(token: string): ApiAction {
  return {
    TockApi: {
      getProto: `/concierge/cancel-verify-email?token=${token}`,
      type: AppConstants.CONCIERGE_CANCEL_BOOK_VIA_EMAIL,
      toast: 'Concierge email reservation has been canceled.',
    },
  };
}

export function calculatePrice(
  purchaseOrder: core.IPurchaseOrder,
  purchase?: core.PastPurchase,
  scope?: ApiScope
): ApiAction {
  return {
    TockApi: {
      postProto: '/ticket/price/consumer',
      request: PurchaseOrder.fromObject(purchaseOrder),
      type: AppConstants.CALCULATE_PRICE,
      scope,
      context: purchase,
    },
  };
}

export function validateDiscountCode(ticketTypeId: number, discountCode: string): ApiAction {
  return {
    TockApi: {
      getProto: `/ticket/type/${ticketTypeId}/promo/code/verify?promoCode=${discountCode}`,
      type: AppConstants.DISCOUNT_CODE_VERIFY,
    },
  };
}

export function clearDiscountCode(): BaseTockAction {
  return {
    type: AppConstants.DISCOUNT_CODE_CLEAR,
  };
}

export function calculatePackagePrice(packagePurchaseOrder: core.IPackagePurchaseOrder): ApiAction {
  return {
    TockApi: {
      postProto: '/ticket/price/consumer/package',
      request: PackagePurchaseOrder.fromObject(packagePurchaseOrder),
      type: AppConstants.CALCULATE_PACKAGE_PRICE,
    },
  };
}

export function updateDiner(diner: core.IPatron | null): BaseTockAction {
  return {
    type: AppConstants.UPDATE_DINER,
    payload: diner,
  };
}

export function purchase(
  purchaseOrder: core.PurchaseOrder,
  scope?: ApiScope,
  ticketType?: core.ITicketType,
  paymentMethodDetails?: PaymentMethodDetails
): ApiAction {
  return {
    TockApi: {
      postProto: '/purchase',
      request: PurchaseOrder.fromObject(purchaseOrder),
      scope,
      type: AppConstants.PURCHASE,
      onSuccess: (state: StoreType, response: any) => {
        const purchasedOrder: core.PurchaseOrder = response.payload;
        window.analytics.sendPurchaseEvent(
          state.app.config?.business,
          purchasedOrder,
          ticketType,
          paymentMethodDetails
        );
        window.dataLayer.push({
          event: 'purchase',
          id: safeToNumber(purchasedOrder.id),
          amount: purchasedOrder.totalPrice,
        });
        if ((window as any).onPurchaseCallback) {
          (window as any).onPurchaseCallback(
            state.app.config?.business,
            purchasedOrder,
            ticketType
          );
        }
      },
      onError: noop,
    },
  };
}

export function submitOptionsToPurchase(
  addOptions: core.IOptionOrder[],
  removeOptions: core.IOptionOrder[],
  basePurchaseId: number,
  visitEditToken: string
): ApiAction {
  return {
    TockApi: {
      putProto: '/purchase/purchased-options',
      request: OptionOrderUpdate.fromObject({
        purchaseId: basePurchaseId,
        optionOrderAdd: addOptions,
        optionOrderRemove: removeOptions,
        visitEditToken,
      }),
      type: AppConstants.PURCHASE,
    },
  };
}

export function cancel(
  purchaseId: number | null,
  businessId: number,
  cancellationToken?: string
): ApiAction {
  const cancelRequest = CancellationRequest.fromObject({ purchaseId, cancellationToken });
  return {
    TockApi: {
      postProto: '/cancel',
      request: cancelRequest,
      scope: { businessId: toNumber(businessId) },
      type: AppConstants.CANCEL_PURCHASE,
      onError: noop, //CancelBookingRoute handles errors, see CancelBookingRoute#handleGenericCancellationError.
    },
  };
}

export function clear(): BaseTockAction {
  return {
    type: AppConstants.PURCHASE_CLEAR,
  };
}

/**
 * The checkout page text.
 * @returns {Object} Redux Tock API action.
 */
export function fetchCheckoutPageDetails(): ApiAction {
  return {
    TockApi: {
      getProto: '/consumer/checkout/details',
      type: AppConstants.CHECKOUT_DETAILS_FETCH,
    },
  };
}

export function fetchSurveyQuestions(ticketTypeId: number, token?: string): ApiAction {
  let url = '/consumer/ticket/type/question';
  if (ticketTypeId) {
    url += `?ticketTypeId=${ticketTypeId}`;
  } else if (token) {
    url += `?token=${token}`;
  } else {
    throw new Error('no ticket type id or token provided');
  }

  return {
    TockApi: {
      getProto: url,
      type: AppConstants.SURVEY_FETCH,
    },
  };
}

export function fetchPastPurchaseTicketTypeDetails(ticketTypeId: number) {
  return (dispatch: Dispatch<ApiAction>) => {
    dispatch(fetchTicketTypeCommunications(ticketTypeId));
    dispatch(fetchSurveyQuestions(ticketTypeId));
  };
}

export function fetchPastPurchase({
  purchaseId,
  token,
}: {
  purchaseId?: number | string;
  token?: string;
}): ApiAction {
  let url = '/consumer/purchase/latest';
  // if (token) is hoisted first because we now decode the JWT token for the purchaseId
  // and call fetchPastPurchase with both the purchaseId & token. Prior to this change
  // you could only have purchaseId or token when calling fetchPastPurchase.
  if (token) {
    url += `?token=${token}`;
  } else if (purchaseId) {
    url += `?purchaseId=${purchaseId}`;
  } else {
    throw new Error('Neither purchase id or token was present for fetchPastPurchase action');
  }

  return {
    TockApi: {
      getProto: url,
      type: AppConstants.PAST_PURCHASE_FETCH,
    },
  };
}

export function clearPastPurchase(): BaseTockAction {
  return {
    type: AppConstants.PAST_PURCHASE_CLEAR,
  };
}

export function clearPastPurchaseTicketTypeDetails(): BaseTockAction {
  return {
    type: AppConstants.PAST_PURCHASE_TICKET_TYPE_DETAILS_CLEAR,
  };
}

export function submitSurvey(purchaseId: number, question: any[], token: string): ApiAction {
  const request = AnsweredPurchaseQuestions.fromObject({
    purchaseId,
    token,
    question,
  });

  return {
    TockApi: {
      postProto: '/consumer/purchase/question',
      request,
      type: AppConstants.SURVEY_SUBMIT,
    },
  };
}

export function fetchTicketTypeCommunications(ticketTypeId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/consumer/ticket/type/${ticketTypeId}/communications`,
      type: AppConstants.TICKET_TYPE_COMMUNICATIONS_FETCH,
    },
  };
}

export function showPaymentError(errorMessage: string): BaseTockAction {
  return {
    type: AppConstants.PAYMENT_ERROR,
    payload: errorMessage,
  };
}

export function hidePaymentError(): BaseTockAction {
  return {
    type: AppConstants.PAYMENT_ERROR,
    payload: null,
  };
}

export function setKeyValues(keyValues: Dictionary<string>): BaseTockAction {
  return {
    payload: keyValues,
    type: AppConstants.PURCHASE_SET_KEY_VALUES,
  };
}

export function upsertPostVisitFeedback(
  visitFeedbackInput: core.IVisitFeedback,
  purchaseId?: number
): ApiAction {
  let path: string;
  if (visitFeedbackInput.purchaseToken) {
    path = '/consumer/visit/feedback';
  } else if (purchaseId) {
    path = `/consumer/visit/feedback?purchaseId=${purchaseId}`;
  } else {
    throw new Error('Upserting post visit feedback requires either a token or a purchase ID');
  }
  return {
    TockApi: {
      postProto: path,
      type: AppConstants.UPSERT_POST_VISIT_FEEDBACK,
      request: VisitFeedback.fromObject(visitFeedbackInput),
    },
  };
}

interface PurchaseArg {
  purchaseId: number;
  type: 'PurchaseArg';
}

interface TokenArg {
  token: string;
  type: 'TokenArg';
}

export type FeedbackArg = PurchaseArg | TokenArg;

function getPostVisitFeedbackPath(arg: FeedbackArg): string {
  switch (arg.type) {
    case 'PurchaseArg':
      return `/consumer/visit/feedback?purchaseId=${arg.purchaseId}`;
    case 'TokenArg':
      return `/consumer/visit/feedback/${arg.token}`;
    default:
      assertNever(arg);
  }
}

export function getPostVisitFeedback(arg: FeedbackArg): ApiAction {
  return {
    TockApi: {
      getProto: getPostVisitFeedbackPath(arg),
      type: AppConstants.UPSERT_POST_VISIT_FEEDBACK,
    },
  };
}
